import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import ApprovalHeader from './components/ApprovalHeader';
import TicketOverviewCard from './components/TicketOverviewCard';
import LineItemsBreakdown from './components/LineItemsBreakdown';
import ApprovalActions from './components/ApprovalActions';
import ApprovalConfirmation from './components/ApprovalConfirmation';
import Icon from '../../components/AppIcon';

const CustomerApprovalPortal = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams?.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [approval, setApproval] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [products, setProducts] = useState([]);
  const [org, setOrg] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedDecision, setSubmittedDecision] = useState(null);

  useEffect(() => {
    if (token) {
      loadApprovalData();
    } else {
      setError('invalid_token');
      setLoading(false);
    }
  }, [token]);

  const loadApprovalData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch approval by token
      const { data: approvalData, error: approvalErr } = await supabase
        ?.from('ticket_customer_approvals')
        ?.select('*')
        ?.eq('approval_token', token)
        ?.single();

      if (approvalErr || !approvalData) {
        setError('not_found');
        return;
      }

      // Check expiry
      if (approvalData?.deadline_at && new Date(approvalData?.deadline_at) < new Date() && approvalData?.status === 'pending') {
        await supabase
          ?.from('ticket_customer_approvals')
          ?.update({ status: 'expired' })
          ?.eq('id', approvalData?.id);
        approvalData.status = 'expired';
      }

      setApproval(approvalData);

      // Fetch full ticket details
      const { data: ticketData, error: ticketErr } = await supabase
        ?.from('tickets')
        ?.select(`
          *,
          customer:customers(*),
          service_type:ticket_service_types(*),
          priority:ticket_priorities(*),
          status:ticket_statuses(*),
          ticket_technicians(
            technician:personnel(id, name, email, phone)
          ),
          ticket_products(*)
        `)
        ?.eq('id', approvalData?.ticket_id)
        ?.single();

      if (ticketErr || !ticketData) {
        setError('ticket_not_found');
        return;
      }

      setTicket(ticketData);
      setProducts(ticketData?.ticket_products || []);

      // Fetch org info
      const { data: orgData } = await supabase
        ?.from('organizations')
        ?.select('name, logo_url, email, phone')
        ?.limit(1)
        ?.single();

      setOrg(orgData);
    } catch (err) {
      console.error('Error loading approval data:', err?.message);
      setError('load_error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitDecision = async ({ decision, comments, signerName, signatureData }) => {
    try {
      setSubmitting(true);

      // Update approval record
      const { error: updateErr } = await supabase
        ?.from('ticket_customer_approvals')
        ?.update({
          status: decision,
          customer_notes: comments,
          customer_signature_name: signerName || null,
          signature_data: signatureData || null,
          decided_at: new Date()?.toISOString(),
          ip_address: null,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', approval?.id);

      if (updateErr) throw updateErr;

      // Log in ticket history
      await supabase?.from('ticket_history')?.insert({
        ticket_id: ticket?.id,
        action: decision === 'approved' ? 'Customer Approved' : 'Customer Rejected',
        description: `Customer ${decision === 'approved' ? 'approved' : 'rejected'} the service ticket via approval portal.${
          comments ? ' Notes: ' + comments : ''
        }${signerName ? ' Signed by: ' + signerName : ''}`,
        user_name: approval?.customer_name || 'Customer',
        icon: decision === 'approved' ? 'CheckCircle' : 'XCircle'
      });

      // If approved, update ticket status to In Progress
      if (decision === 'approved') {
        const { data: inProgressStatus } = await supabase
          ?.from('ticket_statuses')
          ?.select('id')
          ?.ilike('name', 'in progress')
          ?.single();
        if (inProgressStatus) {
          await supabase
            ?.from('tickets')
            ?.update({ status_id: inProgressStatus?.id, approval_status: 'approved' })
            ?.eq('id', ticket?.id);
        }
      } else {
        await supabase
          ?.from('tickets')
          ?.update({ approval_status: 'rejected' })
          ?.eq('id', ticket?.id);
      }

      // Send notification email to org
      try {
        const totalAmount = products?.reduce(
          (sum, item) => sum + (parseFloat(item?.price || 0) * parseInt(item?.quantity || 1)), 0
        );
        await fetch(
          `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-ticket-approval`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
            },
            body: JSON.stringify({
              type: 'ticket_approval_decision',
              recipientEmail: org?.email || 'admin@fieldservicepro.com',
              recipientName: 'Service Team',
              ticketNumber: ticket?.ticket_number,
              customerName: approval?.customer_name,
              decision,
              customerNotes: comments,
              orgName: org?.name,
              totalAmount
            })
          }
        );
      } catch (emailErr) {
        console.error('Notification email failed:', emailErr);
      }

      setSubmittedDecision(decision);
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting decision:', err?.message);
      alert('Failed to submit your decision. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Show confirmation screen after submission
  if (submitted) {
    return (
      <ApprovalConfirmation
        decision={submittedDecision}
        ticketNumber={ticket?.ticket_number}
        customerName={approval?.customer_name}
        orgName={org?.name}
      />
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Loader2" size={28} color="var(--color-primary)" className="animate-spin" />
          </div>
          <p className="text-gray-600 font-medium">Loading approval details...</p>
          <p className="text-gray-400 text-sm mt-1">Please wait a moment</p>
        </div>
      </div>
    );
  }

  // Error states
  if (error) {
    const errorConfig = {
      invalid_token: {
        icon: 'ShieldX',
        title: 'Invalid Access Link',
        message: 'This approval link is invalid or has been tampered with. Please use the original link from your email.',
        color: 'text-red-600',
        bg: 'bg-red-50'
      },
      not_found: {
        icon: 'SearchX',
        title: 'Approval Not Found',
        message: 'This approval request could not be found. It may have been cancelled or the link is incorrect.',
        color: 'text-orange-600',
        bg: 'bg-orange-50'
      },
      ticket_not_found: {
        icon: 'FileX',
        title: 'Ticket Not Found',
        message: 'The service ticket associated with this approval could not be found.',
        color: 'text-orange-600',
        bg: 'bg-orange-50'
      },
      load_error: {
        icon: 'WifiOff',
        title: 'Connection Error',
        message: 'Unable to load the approval details. Please check your connection and try again.',
        color: 'text-gray-600',
        bg: 'bg-gray-50'
      }
    };
    const cfg = errorConfig?.[error] || errorConfig?.load_error;

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className={`px-8 py-10 text-center ${cfg?.bg}`}>
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
              <Icon name={cfg?.icon} size={32} color="currentColor" className={cfg?.color} />
            </div>
            <h2 className={`text-xl font-bold mb-2 ${cfg?.color}`}>{cfg?.title}</h2>
            <p className="text-gray-600 text-sm leading-relaxed">{cfg?.message}</p>
          </div>
          <div className="px-8 py-6 text-center">
            <p className="text-xs text-gray-400">If you believe this is an error, please contact your service representative.</p>
          </div>
        </div>
      </div>
    );
  }

  // Already decided
  const isDecided = approval?.status === 'approved' || approval?.status === 'rejected';
  const isExpired = approval?.status === 'expired';

  return (
    <>
      <Helmet>
        <title>Service Approval - {ticket?.ticket_number} | {org?.name || 'FieldServicePro'}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <ApprovalHeader
          orgName={org?.name}
          orgLogo={org?.logo_url}
          ticketNumber={ticket?.ticket_number}
          status={approval?.status}
        />

        <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
          {/* Already Decided Banner */}
          {isDecided && (
            <div className={`rounded-xl p-4 flex items-start gap-3 ${
              approval?.status === 'approved' ?'bg-emerald-50 border border-emerald-200' :'bg-red-50 border border-red-200'
            }`}>
              <Icon
                name={approval?.status === 'approved' ? 'CheckCircle2' : 'XCircle'}
                size={20}
                color={approval?.status === 'approved' ? '#059669' : '#dc2626'}
                className="flex-shrink-0 mt-0.5"
              />
              <div>
                <p className={`text-sm font-semibold ${
                  approval?.status === 'approved' ? 'text-emerald-800' : 'text-red-800'
                }`}>
                  You have already {approval?.status} this service request
                </p>
                {approval?.decided_at && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Submitted on {new Date(approval?.decided_at)?.toLocaleString()}
                  </p>
                )}
                {approval?.customer_notes && (
                  <p className="text-xs text-gray-600 mt-1 italic">"{approval?.customer_notes}"</p>
                )}
              </div>
            </div>
          )}

          {/* Expired Banner */}
          {isExpired && (
            <div className="rounded-xl p-4 flex items-start gap-3 bg-amber-50 border border-amber-200">
              <Icon name="Clock" size={20} color="#d97706" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800">This approval request has expired</p>
                <p className="text-xs text-gray-600 mt-0.5">
                  The 72-hour response window has passed. Please contact your service representative to request a new approval link.
                </p>
              </div>
            </div>
          )}

          {/* Ticket Overview */}
          <TicketOverviewCard ticket={ticket} approval={approval} />

          {/* Line Items */}
          <LineItemsBreakdown products={products} />

          {/* Approval Actions - only show if pending */}
          {!isDecided && !isExpired && (
            <ApprovalActions
              approval={approval}
              onSubmit={handleSubmitDecision}
              submitting={submitting}
            />
          )}

          {/* Security Footer */}
          <div className="text-center py-4">
            <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
              <span className="flex items-center gap-1">
                <Icon name="Lock" size={11} />
                Secure Connection
              </span>
              <span className="flex items-center gap-1">
                <Icon name="Shield" size={11} />
                Encrypted
              </span>
              <span className="flex items-center gap-1">
                <Icon name="FileCheck" size={11} />
                Audit Logged
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Powered by {org?.name || 'FieldServicePro'}
            </p>
          </div>
        </main>
      </div>
    </>
  );
};

export default CustomerApprovalPortal;
